import React, {
  createContext,
  useState,
  useMemo,
  useEffect,
  useContext,
  useCallback
} from "react";
import PropTypes from "prop-types";
import Dialog from "../../components/dialog";
import ModernizedDialog from "../../components/modernized/modernized-modal";

export const MessagingContext = createContext({
  showDialog: () => {}
});

export function enhanceDialogActionsWith(actions, enhanceWithCb) {
  return actions.map(a => ({
    ...a,
    onClick: () => {
      if (typeof a.onClick === "function") {
        a.onClick();
      }
      enhanceWithCb();
    }
  }));
}

// "Messaging", because potentially can be expanded to handle toast notifications, banners, etc

export function MessagingLayer({
  appData,
  children,
  components,
  globalDialogConfig,
  formRef,
  hyCloseButton,
  headerClass,
  footerClass,
  getModalPosition
}) {
  const [config, setConfig] = useState({});
  const [isModernizedDialog, setModernizedDialog] = useState(false);

  const hideDialog = useCallback(() => {
    setConfig({});
    if (appData) {
      appData.clearDialogFlow({
        formState: appData.history[0]?.formState,
        resettable: config.resettable
      });
    }
  }, [setConfig, appData?.history, config.resettable]);
  const {
    actions = [],
    content,
    fullscreen,
    withClose,
    meta
  } = globalDialogConfig || config;
  const dialogContext = useMemo(
    () => ({
      showDialog: (dialogConfig = {}) =>
        dialogConfig.content?.dialogFields &&
        config.content?.dialogFields === dialogConfig.content?.dialogFields
          ? null
          : setConfig(dialogConfig),
      hideDialog
    }),
    [content, setConfig, hideDialog]
  );

  useEffect(() => {
    if (appData && appData?.data) {
      const {
        data: { screens }
      } = appData;

      const isModernized = screens?.find(screen =>
        screen.params?.includes("TDScreenOptionModernizedDialog")
      );
      setModernizedDialog(isModernized);
    }
  }, [appData]);

  /*
   Make action close dialog after fired.
   Possible enhancements
   1) take 'promisified' actions
      a.onClickPromise().then(() => hideDialog());
   2) add flag if action should not close the dialog
      a.shouldKeep ? <do nothing> : (a.onClick(); hideDialog())
   3) you name it
   */
  const enhancedActions = useMemo(
    () => enhanceDialogActionsWith(actions, hideDialog),
    [actions, hideDialog]
  );

  return (
    <MessagingContext.Provider value={dialogContext}>
      {children}
      {isModernizedDialog ? (
        <ModernizedDialog
          appData={appData}
          components={components}
          active={!!content}
          actions={enhancedActions}
          onClose={hideDialog}
          formRef={formRef}
          headerClass={headerClass}
          footerClass={footerClass}
          meta={meta}
          {...content}
        />
      ) : (
        <Dialog
          appData={appData}
          components={components}
          active={!!content}
          actions={enhancedActions}
          fullscreen={fullscreen}
          onClose={hideDialog}
          withClose={withClose}
          formRef={formRef}
          hyCloseButton={hyCloseButton}
          headerClass={headerClass}
          footerClass={footerClass}
          meta={meta}
          getModalPosition={getModalPosition}
          {...content}
        />
      )}
    </MessagingContext.Provider>
  );
}

export function useDialog() {
  return useContext(MessagingContext);
}

MessagingLayer.propTypes = {
  getModalPosition: PropTypes.func,
  hyCloseButton: PropTypes.objectOf(PropTypes.object),
  headerClass: PropTypes.string,
  footerClass: PropTypes.string,
  formRef: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.func,
      PropTypes.objectOf(PropTypes.any)
    ])
  ).isRequired,
  appData: PropTypes.objectOf(PropTypes.any).isRequired,
  children: PropTypes.node.isRequired,
  components: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.func,
      PropTypes.objectOf(PropTypes.any)
    ])
  ),
  globalDialogConfig: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.object])
  )
};
